import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableFooter,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Fab,
  Tooltip,
  Card,
  CardContent,
  Box,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import { goTo } from '../../shared/utils';
import { formatBrDate, formatMoney } from '../../shared/formatters';
import {
  loadTransaction,
  updateTransactionOrder,
} from '../../redux/transactionSlice';
import Filter from './filter';
import { downloadCSV } from '../../shared/utils.js';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  marginTop: '10px',
  position: 'relative',
  top: '10px',
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'block',
    marginTop: theme.spacing(2),
  },
}));

const TransactionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const TransactionCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2),
  '&:last-child': {
    paddingBottom: theme.spacing(2),
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1.5),
}));

const CardBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '0.875rem',
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  fontWeight: 500,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '0.875rem',
  fontWeight: 400,
}));

const ValueChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'iscredit',
})(({ theme, iscredit }) => ({
  fontWeight: 700,
  fontSize: '1rem',
  padding: theme.spacing(1.5, 1),
  height: 'auto',
  borderRadius: '12px',
  backgroundColor: iscredit 
    ? 'rgba(76, 175, 80, 0.1)' 
    : 'rgba(244, 67, 54, 0.1)',
  color: iscredit 
    ? theme.palette.success.dark 
    : theme.palette.error.dark,
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.1)',
  color: 'rgb(0,98,105)',
  fontWeight: 600,
  borderRadius: '8px',
  fontSize: '0.813rem',
}));

const TotalCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: '16px',
  background: 'rgb(0,98,105)',
  color: 'white',
  boxShadow: '0 8px 24px rgba(0, 98, 105, 0.3)',
}));

const TotalCardContent = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2.5),
  '&:last-child': {
    paddingBottom: theme.spacing(2.5),
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 700,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 98, 105, 0.04)',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.05)',
  '& .MuiTableCell-head': {
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  zIndex: '1000',
  boxShadow: '0 8px 24px rgba(0, 98, 105, 0.4)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 12px 32px rgba(0, 98, 105, 0.5)',
  },
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
  },
}));

const StyledTransferButton = styled(Fab)(({ theme }) => ({
  zIndex: '1000',
  backgroundColor: 'rgb(0,120,128)',
  color: 'white',
  boxShadow: '0 8px 24px rgba(0, 120, 128, 0.4)',
  '&:hover': {
    backgroundColor: 'rgb(0,98,105)',
    transform: 'scale(1.05)',
    boxShadow: '0 12px 32px rgba(0, 120, 128, 0.5)',
  },
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: theme.spacing(10),
    right: theme.spacing(2),
  },
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  gap: theme.spacing(2),
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  flex: 1,
  minWidth: 0,
  flexDirection: 'column',
  alignItems: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
  },
}));

const HeaderRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const VisuallyHidden = styled('span')(({ theme }) => ({
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  top: 20,
  width: 1,
}));

const handleOrder = (updateOrder, event, order, selectedOrder) => {
  event.preventDefault();
  const isAsc = order.by === selectedOrder && order.direction === 'asc';
  var direction = isAsc ? 'desc' : 'asc';
  updateOrder({ by: selectedOrder, direction });
};

function List() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  // Redux state selectors
  const items = useSelector((state) => state.transaction.items);
  const shouldBeUpdated = useSelector(
    (state) => state.transaction.shouldBeUpdated
  );
  const order = useSelector((state) => state.transaction.order);

  const updateOrder = (order) => dispatch(updateTransactionOrder(order));

  useEffect(() => {
    if (shouldBeUpdated) {
      dispatch(loadTransaction());
    }
  }, [shouldBeUpdated, dispatch]);

  function processDataForExport(data) {
    return (data || []).map((item) => {
      const transaction = { ...item };
      if (!!transaction) {
        if (!!transaction.category) {
          if (!!transaction.category.type) {
            transaction.value =
              transaction.category.type === 'credit' ||
              transaction.category.type === 'credit-transfer'
                ? Math.abs(transaction.value)
                : -Math.abs(transaction.value);
          }
          if (!!transaction.category.name)
            transaction.categoryName = transaction.category.name;
        }
        if (!!transaction.financeAccount && !!transaction.financeAccount.name)
          transaction.accountName = transaction.financeAccount.name;
      }
      return transaction;
    });
  }

  const handleExport = () => {
    const columnsMap = [
      { field: 'accountName', title: 'Conta' },
      { field: 'categoryName', title: 'Categoria' },
      { field: 'description', title: 'Descrição' },
      { field: 'date', title: 'Data' },
      { field: 'value', title: 'Valor' },
    ];
    if (items.length > 0) {
      downloadCSV(
        processDataForExport(items),
        columnsMap,
        'relatorio_transacoes.csv'
      );
    }
  };

  const total = items.reduce((total, transaction) => {
    if (
      !!transaction &&
      !!transaction.category &&
      !!transaction.category.type
    ) {
      if (
        transaction.category.type === 'credit' ||
        transaction.category.type === 'credit-transfer'
      )
        return total + transaction.value;
      else return total - transaction.value;
    }
    return total;
  }, 0);

  const isCredit = (categoryType) => {
    return categoryType === 'credit' || categoryType === 'credit-transfer';
  };

  const renderMobileCards = () => (
    <MobileContainer>
      {(items || []).map((item) => {
        const isCreditTransaction = isCredit(item.category.type);
        return (
          <TransactionCard 
            key={item.id}
            onClick={() => goTo(navigate, `transactions/edit/${item.id}`)}
          >
            <TransactionCardContent>
              <CardHeader>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {item.description}
                  </Typography>
                  <CategoryChip 
                    label={item.category.name} 
                    size="small"
                  />
                </Box>
                <ValueChip
                  icon={isCreditTransaction ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  label={
                    (isCreditTransaction ? '+' : '-') + formatMoney(item.value)
                  }
                  iscredit={isCreditTransaction}
                />
              </CardHeader>
              <CardBody>
                <InfoRow>
                  <InfoLabel>Conta</InfoLabel>
                  <InfoValue>{item.financeAccount.name}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Data</InfoLabel>
                  <InfoValue>{formatBrDate(item.date)}</InfoValue>
                </InfoRow>
              </CardBody>
            </TransactionCardContent>
          </TransactionCard>
        );
      })}
      <TotalCard>
        <TotalCardContent>
          <Typography variant="h6" fontWeight={700}>
            Total
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {formatMoney(total)}
          </Typography>
        </TotalCardContent>
      </TotalCard>
    </MobileContainer>
  );

  return (
    <>
      {/* Mobile FABs */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        <Tooltip title="Transferência" aria-label="Transferência">
          <StyledTransferButton
            aria-label="Transfer"
            onClick={() => goTo(navigate, 'transactions/transfer')}
          >
            <SwapHorizIcon />
          </StyledTransferButton>
        </Tooltip>
        <Tooltip title="Nova Transação" aria-label="Nova Transação">
          <StyledFab
            color="primary"
            aria-label="Add"
            onClick={() => goTo(navigate, 'transactions/new')}
          >
            <AddIcon />
          </StyledFab>
        </Tooltip>
      </Box>

      {/* Header with Title, Filters, and Action Buttons */}
      <HeaderContainer>
        <HeaderLeft>
          <Typography variant="h4" component="h2" sx={{ 
            fontWeight: 700,
            color: '#1a1a1a',
            margin: 0,
          }}>
            Transações
          </Typography>
          <Filter onExport={handleExport}></Filter>
        </HeaderLeft>
        
        <HeaderRight>
          <Tooltip title="Transferência" aria-label="Transferência">
            <StyledTransferButton
              aria-label="Transfer"
              onClick={() => goTo(navigate, 'transactions/transfer')}
              size="medium"
            >
              <SwapHorizIcon />
            </StyledTransferButton>
          </Tooltip>
          <Tooltip title="Nova Transação" aria-label="Nova Transação">
            <StyledFab
              color="primary"
              aria-label="Add"
              onClick={() => goTo(navigate, 'transactions/new')}
              size="medium"
            >
              <AddIcon />
            </StyledFab>
          </Tooltip>
        </HeaderRight>
      </HeaderContainer>
      
      {/* Mobile Card View */}
      {renderMobileCards()}
      
      {/* Desktop Table View */}
      <StyledPaper>
        <StyledTable stickyHeader>
          <StyledTableHead>
            <TableRow>
              <StyledTableCell
                sortDirection={order.by === 'account' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'account'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'account')
                  }
                >
                  Conta
                  {order.by === 'account' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="left"
                sortDirection={
                  order.by === 'category' ? order.direction : false
                }
              >
                <TableSortLabel
                  active={order.by === 'category'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'category')
                  }
                >
                  Categoria
                  {order.by === 'category' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="left"
                sortDirection={
                  order.by === 'description' ? order.direction : false
                }
              >
                <TableSortLabel
                  active={order.by === 'description'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'description')
                  }
                >
                  Descrição
                  {order.by === 'description' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="left"
                sortDirection={order.by === 'date' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'date'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'date')
                  }
                >
                  Data
                  {order.by === 'date' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell
                align="right"
                sortDirection={order.by === 'value' ? order.direction : false}
              >
                <TableSortLabel
                  active={order.by === 'value'}
                  direction={order.direction}
                  onClick={(event) =>
                    handleOrder(updateOrder, event, order, 'value')
                  }
                >
                  Valor
                  {order.by === 'value' ? (
                    <VisuallyHidden>
                      {order.direction === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </VisuallyHidden>
                  ) : null}
                </TableSortLabel>
              </StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {(items || []).map((item) => (
              <StyledTableRow
                hover
                key={item.id}
                onClick={() => goTo(navigate, `transactions/edit/${item.id}`)}
              >
                <StyledTableCell component="th" scope="row">
                  {item.financeAccount.name}
                </StyledTableCell>
                <StyledTableCell align="left">{item.category.name}</StyledTableCell>
                <StyledTableCell align="left">{item.description}</StyledTableCell>
                <StyledTableCell align="left">{formatBrDate(item.date)}</StyledTableCell>
                <StyledTableCell 
                  align="right"
                  sx={{
                    color: isCredit(item.category.type) 
                      ? theme.palette.success.dark 
                      : theme.palette.error.dark,
                    fontWeight: 700,
                  }}
                >
                  {(item.category.type === 'debit' ||
                  item.category.type === 'debit-transfer'
                    ? '-'
                    : '+') + formatMoney(item.value)}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <StyledTableCell colSpan={4} sx={{ fontWeight: 700, fontSize: '1rem' }}>
                Total
              </StyledTableCell>
              <StyledTableCell align="right" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
                {formatMoney(total)}
              </StyledTableCell>
            </TableRow>
          </TableFooter>
        </StyledTable>
      </StyledPaper>
    </>
  );
}

export default List;
